import { useCallback, useEffect, useRef, useState } from 'react'

type State =
  | { status: 'idle' }
  | { status: 'working'; startedAt: number }
  | { status: 'error'; message: string }

/**
 * Downloads a file the server has to build first (an Excel export), with a visible "working"
 * state and a readable error, instead of a bare <a href> that gives no feedback while the server
 * works and fails silently when it can't.
 *
 * Error bodies are RFC 7807 problem details from the API; their `detail` is shown as-is.
 */
export function useFileDownload() {
  const [state, setState] = useState<State>({ status: 'idle' })
  const abort = useRef<AbortController | null>(null)
  useEffect(() => () => abort.current?.abort(), [])

  const download = useCallback(async (url: string, fallbackName: string) => {
    abort.current?.abort()
    const ctl = new AbortController()
    abort.current = ctl
    setState({ status: 'working', startedAt: performance.now() })
    try {
      const res = await fetch(url, { signal: ctl.signal, credentials: 'same-origin' })
      if (!res.ok) {
        let message = `Export failed (HTTP ${res.status}).`
        try {
          const p = await res.json() as { detail?: string; title?: string }
          message = p.detail ?? p.title ?? message
        } catch { /* not JSON */ }
        setState({ status: 'error', message })
        return
      }
      const blob = await res.blob()
      const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = name ? decodeURIComponent(name) : fallbackName
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 10_000)
      setState({ status: 'idle' })
    } catch (e) {
      if (ctl.signal.aborted) return
      setState({ status: 'error', message: 'Export failed — the connection dropped. Try again.' })
      console.warn('Export failed', e)
    }
  }, [])

  const reset = useCallback(() => setState({ status: 'idle' }), [])
  return { state, download, reset }
}
